import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";

import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { zoomIn } from "../utils/animations";

const MotionBox = motion(Box);

/*
|--------------------------------------------------------------------------
| CONFIGURATION
|--------------------------------------------------------------------------
|
| Replace these values with your actual WiYou! Tech details.
|
*/

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbxL7k-esBt27qwRKAUkNgjSHnJPRiKuvD_Dtqrh1XcIH4jsbQr_tGE0LO8HHzHaPe-d/exec";

const WHATSAPP_NUMBER = "917689919139";

const PHONE_NUMBER = "+917689919139";

const CONTACT_EMAIL = "officialwiyou.team@gmail.com";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    project: "",
    additionalDetails: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitStatus, setSubmitStatus] = useState({
    type: "",
    message: "",
  });

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "#FBF9F5",
      fontSize: "14px",
      transition: "all 220ms ease",

      "& fieldset": {
        borderColor: "#DCD4CC",
        transition:
          "border-color 220ms ease, box-shadow 220ms ease",
      },

      "&:hover fieldset": {
        borderColor: "#A06E50",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#3E2723",
        borderWidth: "1px",
      },

      "&.Mui-focused": {
        boxShadow:
          "0 0 0 4px rgba(160, 110, 80, 0.08)",
      },
    },

    "& .MuiInputLabel-root": {
      color: "#75675E",
      fontSize: "14px",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#3E2723",
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!GOOGLE_SCRIPT_URL || GOOGLE_SCRIPT_URL.includes("YOUR_")) {
      setSubmitStatus({
        type: "error",
        message:
          "Contact form is not configured yet. Please add your Google Apps Script URL.",
      });

      return;
    }

    setIsSubmitting(true);

    setSubmitStatus({
      type: "",
      message: "",
    });

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        project: formData.project.trim(),
        additionalDetails:
          formData.additionalDetails.trim(),
        source: "WiYou! Tech Website",
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        headers: {
          "Content-Type":
            "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(
          result.message ||
            "Unable to submit the form."
        );
      }

      setSubmitStatus({
        type: "success",
        message:
          "Your message has been submitted successfully. We've sent a confirmation email to you.",
      });

      setFormData({
        name: "",
        email: "",
        project: "",
        additionalDetails: "",
      });
    } catch (error) {
      console.error(
        "Contact form submission error:",
        error
      );

      setSubmitStatus({
        type: "error",
        message:
          "Something went wrong while sending your message. Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSubmitStatus({
      type: "",
      message: "",
    });
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        maxWidth: "1560px",
        mx: "auto",
        mt: 12,
        px: {
          xs: 2,
          sm: 3,
          md: 5,
        },
        mb: {
          xs: 5,
          md: 8,
        },
      }}
    >
      <MotionBox
        variants={zoomIn}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.2,
        }}
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          borderRadius: {
            xs: "24px",
            md: "34px",
          },
          backgroundColor: "#F7F4EF",
          border: "1px solid #DCD4CC",
        }}
      >

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "0.85fr 1.15fr",
            },
            minHeight: {
              lg: "680px",
            },
          }}
        >
          {/* =====================================================
              LEFT SIDE
          ===================================================== */}

          <Box
            sx={{
              position: "relative",
              p: {
                xs: 3,
                sm: 4,
                md: 5,
                lg: 6,
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              borderRight: {
                lg: "0px solid #DCD4CC",
              },
              borderBottom: {
                xs: "0px solid #DCD4CC",
                lg: "none",
              },
            }}
          >

            <Box>

              {/* Heading */}
              <Typography
                component="h2"
                sx={{
                  maxWidth: "560px",
                  fontSize: {
                    xs: "38px",
                    sm: "48px",
                    md: "56px",
                    lg: "62px",
                  },
                  lineHeight: 0.98,
                  fontWeight: 800,
                  letterSpacing:
                    "-0.055em",
                  color: "#2B211C",
                  mb: 3,
                }}
              >
                Have an idea?
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: "#A06E50",
                  }}
                >
                  Let's build it.
                </Box>
              </Typography>

              <Typography
                sx={{
                  maxWidth: "470px",
                  fontSize: {
                    xs: "15px",
                    md: "16px",
                  },
                  lineHeight: 1.8,
                  color: "#75675E",
                }}
              >
                Tell us what you're working
                on, what you're trying to
                solve, or simply where you
                want to go next. We'll turn
                the idea into something clear,
                useful and beautifully built.
              </Typography>
            </Box>

            {/* =================================================
                DIRECT CONTACT
            ================================================= */}

            {/* Decorative number */}
            <Typography
              sx={{
                position: "absolute",
                top: {
                  xs: 140,
                  md: 385,
                },
                right: {
                  xs: -145,
                  md: -213,
                },
                fontSize: {
                  xs: "52px",
                  md: "80px",
                },
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-0.08em",
                width: "max-content",
                color:
                  "rgba(62, 39, 35, 0.045)",
                userSelect: "none",
                transform: "rotate(-90deg)",
              }}
            >
              CONTACT US!
            </Typography>

            <Box
              sx={{
                mt: {
                  xs: 5,
                  lg: 6,
                },
              }}
            >

              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing:
                    "0.12em",
                  textTransform:
                    "uppercase",
                  color: "#A06E50",
                  mb: 1.5,
                }}
              >
                Or reach us directly
              </Typography>

              <Stack spacing={1.2}>
                {/* Call */}
                <Box
                  component="a"
                  href={`tel:${PHONE_NUMBER}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    p: 1.5,
                    borderRadius: "14px",
                    border:
                      "0px solid #DCD4CC",
                    backgroundColor:
                      "#FCFAF6",
                      backdropFilter: 'blur(10px)',
                    color: "inherit",
                    textDecoration: "none",
                    transition:
                      "all 220ms ease",

                    "&:hover": {
                      borderColor:
                        "#A06E50",
                      transform:
                        "translateX(4px)",
                      backgroundColor:
                        "#fef6ef",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems:
                        "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius:
                          0.35,
                        display: "grid",
                        placeItems:
                          "center",
                        backgroundColor:
                          "#f7ece3",
                        color:
                          "#3E2723",
                      }}
                    >
                      <PhoneRoundedIcon
                        sx={{
                          fontSize: 18,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                            "13px",
                          fontWeight: 700,
                          color:
                            "#3E2723",
                        }}
                      >
                        Call us
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            "11px",
                          color:
                            "#75675E",
                        }}
                      >
                        {PHONE_NUMBER}
                      </Typography>
                    </Box>
                  </Box>

                  <ArrowOutwardRoundedIcon
                    sx={{
                      fontSize: 17,
                      color: "#A06E50",
                    }}
                  />
                </Box>

                {/* WhatsApp */}
                <Box
                  component="a"
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    p: 1.5,
                    borderRadius: "14px",
                    border:
                      "0px solid #DCD4CC",
                    backgroundColor:
                      "#FCFAF6",
                      backdropFilter: 'blur(10px)',
                    color: "inherit",
                    textDecoration: "none",
                    transition:
                      "all 220ms ease",

                    "&:hover": {
                      borderColor:
                        "#A06E50",
                      transform:
                        "translateX(4px)",
                      backgroundColor:
                        "#F7F1EB",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems:
                        "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius:
                          0.35,
                        display: "grid",
                        placeItems:
                          "center",
                        backgroundColor:
                          "#f7ece3",
                        color:
                          "#3E2723",
                      }}
                    >
                      <WhatsAppIcon
                        sx={{
                          fontSize: 18,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                            "13px",
                          fontWeight: 700,
                          color:
                            "#3E2723",
                        }}
                      >
                        WhatsApp
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            "11px",
                          color:
                            "#75675E",
                        }}
                      >
                        Chat with our team
                      </Typography>
                    </Box>
                  </Box>

                  <ArrowOutwardRoundedIcon
                    sx={{
                      fontSize: 17,
                      color: "#A06E50",
                    }}
                  />
                </Box>

                {/* Email */}
                <Box
                  component="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    p: 1.5,
                    borderRadius: "14px",
                    border:
                      "0px solid #DCD4CC",
                    backgroundColor:
                      "#FCFAF6",
                      backdropFilter: 'blur(10px)',
                    color: "inherit",
                    textDecoration: "none",
                    transition:
                      "all 220ms ease",

                    "&:hover": {
                      borderColor:
                        "#A06E50",
                      transform:
                        "translateX(4px)",
                      backgroundColor:
                        "#F7F1EB",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems:
                        "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius:
                          0.35,
                        display: "grid",
                        placeItems:
                          "center",
                        backgroundColor:
                          "#f7ece3",
                        color:
                          "#3E2723",
                      }}
                    >
                      <MailOutlineRoundedIcon
                        sx={{
                          fontSize: 18,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                            "13px",
                          fontWeight: 700,
                          color:
                            "#3E2723",
                        }}
                      >
                        Email us
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:
                            "11px",
                          color:
                            "#75675E",
                        }}
                      >
                        {CONTACT_EMAIL}
                      </Typography>
                    </Box>
                  </Box>

                  <ArrowOutwardRoundedIcon
                    sx={{
                      fontSize: 17,
                      color: "#A06E50",
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* =====================================================
              RIGHT SIDE — FORM
          ===================================================== */}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: {
                xs: 3,
                sm: 4,
                md: 5,
                lg: 6,
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              backgroundColor: "#FCFAF6",
            }}
          >
            {/* Form heading */}
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing:
                    "0.12em",
                  textTransform:
                    "uppercase",
                  color: "#A06E50",
                  mb: 1,
                }}
              >
                Start a conversation
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: "27px",
                    md: "32px",
                  },
                  fontWeight: 750,
                  letterSpacing:
                    "-0.035em",
                  color: "#2B211C",
                  mb: 1,
                }}
              >
                Tell us what you're
                building.
              </Typography>

              <Typography
                sx={{
                  maxWidth: "520px",
                  fontSize: "14px",
                  lineHeight: 1.7,
                  color: "#75675E",
                }}
              >
                A few details are enough
                to get the conversation
                started.
              </Typography>
            </Box>

            {/* Name + Email */}
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                label="Your name"
                variant="outlined"
                required
                size="medium"
                autoComplete="name"
                sx={fieldStyles}
              />

              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                label="Email address"
                variant="outlined"
                type="email"
                required
                size="medium"
                autoComplete="email"
                sx={fieldStyles}
              />
            </Stack>

            {/* Project */}
            <TextField
              name="project"
              value={formData.project}
              onChange={handleChange}
              fullWidth
              label="What are you working on?"
              variant="outlined"
              multiline
              minRows={5}
              required
              sx={{
                ...fieldStyles,
                mb: 2,
              }}
            />

            {/* Additional details */}
            <TextField
              name="additionalDetails"
              value={
                formData.additionalDetails
              }
              onChange={handleChange}
              fullWidth
              label="Anything else you'd like us to know?"
              variant="outlined"
              multiline
              minRows={3}
              sx={fieldStyles}
            />

            {/* Submit */}
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop:
                  "1px solid #DCD4CC",
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={2}
                alignItems={{
                  xs: "stretch",
                  sm: "center",
                }}
                justifyContent="space-between"
              >
                <Typography
                  sx={{
                    maxWidth: "320px",
                    fontSize: "11px",
                    lineHeight: 1.6,
                    color: "#8A7D74",
                  }}
                >
                  Your details are only
                  used to respond to your
                  enquiry. No spam, no
                  unnecessary follow-ups.
                </Typography>

                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting}
                  endIcon={
                    isSubmitting ? (
                      <CircularProgress
                        size={17}
                        sx={{
                          color:
                            "#FFFDF8",
                        }}
                      />
                    ) : (
                      <ArrowOutwardRoundedIcon
                        sx={{
                          fontSize:
                            "18px !important",
                        }}
                      />
                    )
                  }
                  sx={{
                    minWidth: {
                      xs: "100%",
                      sm: "190px",
                    },
                    minHeight: "50px",
                    px: 3,
                    borderRadius: "14px",
                    backgroundColor:
                      "#3E2723",
                    color: "#FFFDF8",
                    textTransform:
                      "none",
                    fontSize: "14px",
                    fontWeight: 700,
                    letterSpacing:
                      "-0.01em",
                    transition:
                      "transform 220ms ease, background-color 220ms ease, box-shadow 220ms ease",

                    "&:hover": {
                      backgroundColor:
                        "#2B211C",
                      transform:
                        "translateY(-2px)",
                      boxShadow:
                        "0 12px 28px rgba(43, 33, 28, 0.16)",
                    },

                    "&:disabled": {
                      backgroundColor:
                        "#6E5B4D",
                      color: "#FFFDF8",
                    },
                  }}
                >
                  {isSubmitting
                    ? "Sending..."
                    : "Send message"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </MotionBox>

      {/* =========================================================
          SUCCESS / ERROR NOTIFICATION
      ========================================================= */}

      <Snackbar
        open={Boolean(
          submitStatus.message
        )}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={
            submitStatus.type ||
            "info"
          }
          variant="filled"
          sx={{
            borderRadius: "14px",
            fontSize: "13px",
          }}
        >
          {submitStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;